import { ConsumableLine, ConsumablePricingContext, ConsumablePricingResult } from './consumable-models';

// ===== consumable-rule-base.ts ==================================================
export interface ConsumableRule {
    name: string;
    priority: number; // Lower numbers run first
    
    /**
     * Check if this rule applies to the given consumable line
     */
    applies(line: ConsumableLine, ctx: ConsumablePricingContext): boolean;
    
    /**
     * Apply the rule to calculate pricing
     */
    apply(
        line: ConsumableLine,
        ctx: ConsumablePricingContext
    ): ConsumablePricingResult;
}

export abstract class BaseConsumableRule implements ConsumableRule {
    abstract name: string;
    abstract priority: number;
    
    abstract applies(line: ConsumableLine, ctx: ConsumablePricingContext): boolean;
    abstract apply(line: ConsumableLine, ctx: ConsumablePricingContext): ConsumablePricingResult;
    
    /**
     * Helper to calculate base price per unit from pack information
     */
    protected calculateBasePricePerUnit(line: ConsumableLine): number {
        if (!line.standardPacksize || line.standardPacksize <= 0) {
            return line.singleExitPrice || 0;
        }
        return (line.singleExitPrice || 0) / line.standardPacksize;
    }
    
    /**
     * Helper to apply VAT.
     *
     * NOTE: currently DEAD CODE — no rule calls this, and no rule reads ctx.vatRate. Every
     * rule's trace says "VAT will be applied separately", but nothing downstream adds it
     * either: the invoice treats line amounts as already VAT-inclusive and decomposes them.
     * So consumable prices are whatever the Medprax SEP basis is, times the doctor markup.
     * Left in place because ctx.vatRate is now correctly gated on the group's VAT status —
     * if this hook is ever wired up it will do the right thing. Do not assume it runs.
     */
    protected applyVAT(price: number, vatRate: number = 0.15): number {
        return price * (1 + vatRate);
    }
    
    /**
     * Helper to apply doctor markup
     */
    protected applyMarkup(price: number, modifier: number = 100): number {
        const multiplier = modifier / 100;
        return price * multiplier;
    }
    
    /**
     * Create a base result with trace
     */
    protected createResult(
        line: ConsumableLine,
        calculatedUnitPrice: number,
        calculatedTotalPrice: number,
        traceMessage: string
    ): ConsumablePricingResult {
        return {
            line: { ...line },
            calculatedUnitPrice,
            calculatedTotalPrice,
            trace: [traceMessage],
            warnings: []
        };
    }
}

